import { Dialog } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import AuditInfo, { AuditInfoViewModel } from "../../components/AuditInfo";
import CenterDivPanel from "../../components/CenterDivPanel";
import { EditableField } from "../../components/EditableField";
import { fetchApi } from "../../services/api";
import { IDName } from '../../types/idname';
import { Title } from '../../utils/title';
import OwnerAutoComplete, { GetOwnerAutoCompleteDisplayValue } from "../AssetOwners/OwnerAutoComplete";
import { OwnerViewModel } from "../Assets/OwnerGrid";
import { TrailerNavBar } from "./NavBar";
import { JsonResponse } from "TypeGen/json-response";
import { Link } from "react-router-dom";

type Props = {
    isDialog?: boolean
    trailerId?: number;

    onClose?: () => void;
}

type RouteComponentParams = {
    trailerId: string;
};

type TrailerProfileType = {
    Trailer: TrailerType,
    Users: IDName[];
}

type TrailerType = {
    TrailerNumber: string;
    Active: boolean;
    TrailerType: number;
    Payload: number;
    MTWeight: number;
    GrossWeight: number;
    Axles: number;
    BoxLMin: number;
    BoxLMax: number;
    BoxH: number;
    BoxW: number;
    DoorH: number;
    DoorW: number;
    DoorType: string;
    AirRide: boolean;
    TempControl: boolean;
    EscortsRequired: boolean;
    NLMTruckSize: number;
    TrailerYear: number;
    TrailerMake: string;
    TrailerVIN: string;
    SatType: number;
    QualcommMCT: number;
    LicensePlate: string;
    LicenseExpiryDate: Date | null;
    LicenseState: string;
    TrailerBaseLicense: string;
    InsuredBy: string;
    InsurancePolicy: string;
    InsuranceExpiryDate: Date | null;
    OwnedBy: number;
    LeasedFrom: string;
    LeaseStartDate: Date | null;
    LeaseExpiryDate: Date | null;
    LastMthlyInspDate: Date | null;
    NextInspectionDate: Date | null;
    NextPMDate: Date | null;
    Comment1: string;
    Comment2: string;
    OwnerID: number;
    AuditInfo: AuditInfoViewModel;
    OwnerInfo: OwnerViewModel;
}

const NLMTruckSizes = [{ ID: 0, Name: 'None' }, { ID: 1, Name: 'Pick-up Truck' }, { ID: 2, Name: 'Van, Mini' }, { ID: 3, Name: 'Van, Econo' }, { ID: 4, Name: 'Box Truck' }, { ID: 5, Name: '12 Ft. Straight Truck' }, { ID: 6, Name: '13 Ft. Straight Truck' }, { ID: 7, Name: '14 Ft. Straight Truck' }, { ID: 8, Name: '15 Ft. Straight Truck' }, { ID: 9, Name: '16 Ft. Flatbed Truck' }, { ID: 10, Name: '16 Ft. Straight Truck' }, { ID: 11, Name: '18 Ft. Straight Truck' }, { ID: 307, Name: '* 18 Ft. Tandem Straight Truck' }, { ID: 308, Name: '* 20 Ft. Tandem Straight Truck' }, { ID: 309, Name: '* 22 Ft. Tandem Straight Truck' }, { ID: 276, Name: '* 24 Ft. Trailer' }, { ID: 12, Name: 'Dock High' }, { ID: 13, Name: '19 Ft. Straight Truck' }, { ID: 14, Name: '20 Ft. Air-Ride Straight Truck' }, { ID: 15, Name: '20 Ft. Straight Truck' }, { ID: 16, Name: '21 Ft. Straight Truck' }, { ID: 17, Name: '22 Ft. 102 Wide' }, { ID: 18, Name: '22 Ft. Flatbed Truck' }, { ID: 19, Name: '22 Ft. Straight Truck' }, { ID: 20, Name: '23 Ft. Straight Truck' }, { ID: 21, Name: '24 Ft. 102 Wide' }, { ID: 22, Name: '24 Ft. Air-Ride Straight Truck' }, { ID: 23, Name: '24 Ft. Barn Door' }, { ID: 24, Name: '24 Ft. Flatbed Truck' }, { ID: 25, Name: '24 Ft. Straight Truck' }, { ID: 42, Name: '24 Ft. Tandem' }, { ID: 26, Name: '25 Ft. Straight Truck' }, { ID: 27, Name: '26 Ft. 102 Wide' }, { ID: 28, Name: '26 Ft. Straight Truck' }, { ID: 29, Name: '27 Ft. Straight Truck' }, { ID: 30, Name: '28 Ft. Trailer Wide Body' }, { ID: 31, Name: '28 Ft. Straight Truck' }, { ID: 277, Name: '* 28 Ft. Pup' }, { ID: 278, Name: '* 48 Ft. Eight Axle' }, { ID: 279, Name: '* 48 Ft. Five Axle' }, { ID: 280, Name: '* 48 Ft. Quad Axle' }, { ID: 281, Name: '* 48 Ft. Reefer' }, { ID: 282, Name: '* 48 Ft. Six Axle' }, { ID: 283, Name: '* 48 Ft. Tri Axle' }, { ID: 284, Name: '* 48 Ft. Van or Pups' }, { ID: 285, Name: '* 50 Ton Dump Truck' }, { ID: 286, Name: '* 53 Ft. Plated Trailer' }, { ID: 287, Name: '* 53 Ft. Super Van' }, { ID: 288, Name: '* 53 Ft. Van Climate' }, { ID: 289, Name: '* 53 Ft. Drop Floor' }, { ID: 290, Name: '* Double Dump' }, { ID: 291, Name: '* Dump Truck' }, { ID: 292, Name: '* 53 Ft. Step Deck' }, { ID: 310, Name: '* 53 Ft. Conventional' }, { ID: 311, Name: '* 53 Ft. Drop Decks' }, { ID: 312, Name: '* 53 Ft. Air Ride' }, { ID: 273, Name: '* 53 Ft. Drop Deck' }, { ID: 275, Name: '* 53 Ft. Reinforced Trailer' }, { ID: 32, Name: '43 Ft. Van Trailer' }, { ID: 33, Name: '45 Ft. Van Trailer' }, { ID: 34, Name: '48 Ft. Van Trailer' }, { ID: 35, Name: '48 Ft. Flat Bed Trailer' }, { ID: 36, Name: '48 Ft. Van Wide-Body' }, { ID: 37, Name: '48 Ft. Van Wide-Body Air' }, { ID: 38, Name: '50 Ft. Van Trailer' }, { ID: 39, Name: '53 Ft. Van Trailer' }, { ID: 40, Name: '53 Ft. Van Trailer Air' }] as IDName[];
const TrailerTypes = [{ "ID": 1, "Name": "48 Foot Dry Van" }, { "ID": 2, "Name": "53 Foot Dry Van" }, { "ID": 3, "Name": "Flat Bed" }, { "ID": 4, "Name": "Sided Flat Bed" }, { "ID": 5, "Name": "Expandible Flat Bed" }, { "ID": 6, "Name": "Single Drop Deck" }, { "ID": 7, "Name": "Expandible Single Drop Deck" }, { "ID": 8, "Name": "Double Drop Deck" }, { "ID": 9, "Name": "Expandible Double Drop Deck" }, { "ID": 10, "Name": "Gooseneck Low Boy" }, { "ID": 11, "Name": "250 Ton Low Boy" }, { "ID": 12, "Name": "Curtainside Robotic Van" }, { "ID": 13, "Name": "75 Ton Multi Axle Low Boy" }, { "ID": 14, "Name": "Tanker" }, { "ID": 15, "Name": "Stake Truck Flat Bed" }, { "ID": 16, "Name": "53 ft Roller Bed Trailer" }, { "ID": 17, "Name": "53 ft Reefer" }, { "ID": 18, "Name": "Specialized" }, { "ID": 19, "Name": "Flatbed Curtain" }, { "ID": 20, "Name": "Stepdeck Curtain" }, { "ID": 21, "Name": "48 ft Reefer" }, { "ID": 22, "Name": "Dump Trailer" }, { "ID": 23, "Name": "53-ft Plated Trailer" }, { "ID": 24, "Name": "53-ft Reinforced Trailer" }, { "ID": 25, "Name": "53 ft Household Goods" }, { "ID": 26, "Name": "53 ft Flat Floor / Freight" }, { "ID": 27, "Name": "53 ft Logistics Trailer Equipped" }, { "ID": 28, "Name": "51 ft Tri low step CA Legal" }, { "ID": 29, "Name": "48 ft Murray Wheeler" }, { "ID": 30, "Name": "48 ft Murray Triaxle" }, { "ID": 31, "Name": "CA 9axle 60T 16W Jeep" }, { "ID": 32, "Name": "EXT Low 9 axle 60T 8W jeep" }, { "ID": 33, "Name": "51 ft Self-LD Tri-Low Step CA Legal" }, { "ID": 34, "Name": "Step Deck" }, { "ID": 35, "Name": "53 Ft. Vented Plate Trailer" }, { "ID": 36, "Name": "PUP Trailer" }, { "ID": 37, "Name": "Dry Tanker" }, { "ID": 38, "Name": "Liquid Tanker" }, { "ID": 39, "Name": "20' Container Chassis" }, { "ID": 40, "Name": "40' Container Chassis" }, { "ID": 41, "Name": "Conestoga" }, { "ID": 42, "Name": "60 Ton 10 axle" }, { "ID": 43, "Name": "35 Ton Mini Deck" }, { "ID": 44, "Name": "60 Ton 32 wheeler" }, { "ID": 45, "Name": "CA 9axle 65T 16W Jeep" }, { "ID": 46, "Name": "Tandem Jeep" }, { "ID": 47, "Name": "Single Jeep" }, { "ID": 48, "Name": "Tandem booster Hyd Nitro 2nd axle" }, { "ID": 49, "Name": "Tandem booster Hyd relief" }, { "ID": 50, "Name": "Single axle booster pin on" }, { "ID": 51, "Name": "Expandable low bed" }] as IDName[];
const OwnedBy = [{ ID: 1, Name: 'Company' }, { ID: 2, Name: 'Rented' }, { ID: 3, Name: 'Leased' }, { ID: 4, Name: 'Owner/Operator' }] as IDName[];
const DoorTypes = [{ ID: 'B', Name: 'Barn' }, { ID: 'R', Name: 'Roll' }, { ID: 'N', Name: 'N/A' }];
//const CommunicationTypes = [{ ID: 0, Name: 'None' }, { ID: 1, Name: 'Omnitracs TrailerTracs' }, { ID: 2, Name: 'SkyBitz' }] as IDName[];

const TrailerProfile = (props: Props) => {

    const { trailerId: trailerIdParam } = useParams<RouteComponentParams>();
    const [trailerId, setTrailerId] = useState(0);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<TrailerProfileType>();

    const fetchProfile = useCallback(() => {
        async function fetchData() {
            setLoading(true);
            await fetchApi(`/api/Asset/Trailer/${trailerId}`)
                .then(data => {
                    setLoading(false);
                    setProfile(data);
                })
                .catch(err => {
                    alert(err);
                    setLoading(false);
                });
        }
        fetchData();
    }, [trailerId]);

    useEffect(() => {
        if (props.trailerId > 0)
            setTrailerId(props.trailerId);
        else
            setTrailerId(Number(trailerIdParam));
    }, [trailerIdParam, props.trailerId]);

    useEffect(() => {
        if (trailerId > 0)
            fetchProfile();
    }, [trailerId, fetchProfile]);

    const saveAsyncProfile = async (value: Partial<TrailerType>): Promise<void> => {
        let errorMsg = '';
        const newProfile: TrailerType = Object.assign({}, profile.Trailer, value);
        await fetchApi(`/api/Asset/Trailer/${trailerId}`, newProfile, 'PUT')
            .then((response: JsonResponse) => {
                if (response.Success === false) {
                    errorMsg = response.ErrorMessage;
                } else {
                    updateProfileState(value);
                }
            }).catch((e) => {
                if (e?.status) {
                    return Promise.reject(e);
                } else {
                    errorMsg = "Unknown Error Occurred!";
                }
            });
        if (errorMsg.length) {
            alert(errorMsg);
            return Promise.reject(errorMsg);
        }
        return Promise.resolve();
    }

    const updateProfileState = (value: Partial<TrailerType>): TrailerType => {
        const newProfile: TrailerType = Object.assign({}, profile.Trailer, value);
        setProfile({ ...profile, Trailer: newProfile });
        return newProfile;
    }

    const dataView = () => {
        if (loading || !profile) {
            return <CenterDivPanel>
                <Loader type="converging-spinner" />
            </CenterDivPanel>;
        }

        return (<>
            {props.isDialog !== true && trailerId > 0 && <TrailerNavBar id={trailerId} currentTab="Profile" />}
            <Title string={profile.Trailer.TrailerNumber} />
            <div className="row m-2">
                <div className="col-md-6">
                    <div className="form-group row">
                        <label htmlFor="TrailerNumber" className="col-sm-2 col-form-label">Trailer ID</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="string"
                                inputProps={{ maxLength: 12 }}
                                data={profile.Trailer.TrailerNumber}
                                save={async (data) => await saveAsyncProfile({ TrailerNumber: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerType" className="col-sm-2 col-form-label">Trailer Type</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={profile.Trailer.TrailerType}
                                renderDisplay={(data) => TrailerTypes.find(x => x.ID == data).Name}
                                renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(parseInt(e.target.value))}>
                                    {TrailerTypes.map(({ ID, Name }) => <option key={ID} value={ID}>{Name}</option>)}
                                </select>}
                                save={async (data) => await saveAsyncProfile({ TrailerType: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerType" className="col-sm-2 col-form-label">NLM Truck Type</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={profile.Trailer.NLMTruckSize}
                                renderDisplay={(data) => NLMTruckSizes.find(x => x.ID == data)?.Name ?? 'None'}
                                renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(parseInt(e.target.value))}>
                                    {NLMTruckSizes.map(({ ID, Name }) => <option key={ID} value={ID}>{Name}</option>)}
                                </select>}
                                save={async (data) => await saveAsyncProfile({ NLMTruckSize: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="Payload" className="col-sm-2 col-form-label">Payload</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0, placeholder: "lbs" }}
                                data={profile.Trailer.Payload}
                                save={async (data) => await saveAsyncProfile({ Payload: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="MTWeight" className="col-sm-2 col-form-label">Empty Weight</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0, placeholder: "lbs" }}
                                data={profile.Trailer.MTWeight}
                                save={async (data) => await saveAsyncProfile({ MTWeight: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="GrossWeight" className="col-sm-2 col-form-label">Gross Weight</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0, placeholder: "lbs" }}
                                data={profile.Trailer.GrossWeight}
                                save={async (data) => await saveAsyncProfile({ GrossWeight: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="Axles" className="col-sm-2 col-form-label">No. of Axles</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0 }}
                                data={profile.Trailer.Axles}
                                save={async (data) => await saveAsyncProfile({ Axles: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="BoxLMin" className="col-sm-2 col-form-label">Min Box Length</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0, placeholder: "inches" }}
                                data={profile.Trailer.BoxLMin}
                                save={async (data) => await saveAsyncProfile({ BoxLMin: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="BoxLMax" className="col-sm-2 col-form-label">Max Box Length</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 0, placeholder: "inches" }}
                                data={profile.Trailer.BoxLMax}
                                save={async (data) => await saveAsyncProfile({ BoxLMax: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="BoxDims" className="col-sm-2 col-form-label">Box Dims</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={{ BoxW: profile.Trailer.BoxW, BoxH: profile.Trailer.BoxH }}
                                renderDisplay={(data) => `${data.BoxW} x ${data.BoxH}`}
                                renderEditable={(data, saving, onChange) => <>
                                    <input autoFocus required disabled={saving} className="form-control" placeholder="Width (inches)" type="number" min={0} value={data.BoxW} onChange={(e) => onChange({ BoxW: parseInt(e.target.value), BoxH: data.BoxH })} />
                                    <input required disabled={saving} className="form-control" placeholder="Height (inches)" type="number" min={0} value={data.BoxH} onChange={(e) => onChange({ BoxW: data.BoxW, BoxH: parseInt(e.target.value) })} />
                                </>}
                                save={async (data) => await saveAsyncProfile({ BoxW: data.BoxW, BoxH: data.BoxH })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="DoorDims" className="col-sm-2 col-form-label">Door Dims</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={{ DoorW: profile.Trailer.DoorW, DoorH: profile.Trailer.DoorH }}
                                renderDisplay={(data) => `${data.DoorW} x ${data.DoorH}`}
                                renderEditable={(data, saving, onChange) => <>
                                    <input autoFocus required disabled={saving} className="form-control" placeholder="Width (inches)" type="number" min={0} value={data.DoorW} onChange={(e) => onChange({ DoorW: parseInt(e.target.value), DoorH: data.DoorH })} />
                                    <input required disabled={saving} className="form-control" placeholder="Height (inches)" type="number" min={0} value={data.DoorH} onChange={(e) => onChange({ DoorW: data.DoorW, DoorH: parseInt(e.target.value) })} />
                                </>}
                                save={async (data) => await saveAsyncProfile({ DoorW: data.DoorW, DoorH: data.DoorH })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="DoorType" className="col-sm-2 col-form-label">Door Type</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={profile.Trailer.DoorType}
                                renderDisplay={(data) => DoorTypes.find(x => x.ID == data).Name}
                                renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(e.target.value)}>
                                    {DoorTypes.map(({ ID, Name }) => <option key={ID} value={ID}>{Name}</option>)}
                                </select>}
                                save={async (data) => await saveAsyncProfile({ DoorType: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="AirRide" className="col-sm-2 col-form-label">Air Ride</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="boolean"
                                data={profile.Trailer.AirRide}
                                save={async (data) => await saveAsyncProfile({ AirRide: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TempControl" className="col-sm-2 col-form-label">Temp Control</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="boolean"
                                data={profile.Trailer.TempControl}
                                save={async (data) => await saveAsyncProfile({ TempControl: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="EscortsRequired" className="col-sm-2 col-form-label">Escort Req'd</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="boolean"
                                data={profile.Trailer.EscortsRequired}
                                save={async (data) => await saveAsyncProfile({ EscortsRequired: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerYear" className="col-sm-2 col-form-label">Model Year</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="number"
                                inputProps={{ min: 1900, max: 2999, format: '0' }}
                                data={profile.Trailer.TrailerYear}
                                save={async (data) => await saveAsyncProfile({ TrailerYear: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerMake" className="col-sm-2 col-form-label">Make</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="string"
                                inputProps={{ maxLength: 20 }}
                                data={profile.Trailer.TrailerMake}
                                save={async (data) => await saveAsyncProfile({ TrailerMake: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerVIN" className="col-sm-2 col-form-label">VIN</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="string"
                                inputProps={{ maxLength: 25 }}
                                data={profile.Trailer.TrailerVIN}
                                save={async (data) => await saveAsyncProfile({ TrailerVIN: data })}
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group row">
                        <label htmlFor="AssignedOwner" className="col-sm-2 col-form-label">Assigned Owner</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={profile.Trailer.OwnerInfo}
                                renderDisplay={(data) => (data != null ? GetOwnerAutoCompleteDisplayValue(data) : "None")}
                                renderEditable={(data, saving, onChange) =>
                                    <OwnerAutoComplete
                                        disabled={saving}                                        
                                        placeholder='type an owner id, name or leave blank...'
                                        style={{ flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                        selectedOwnerID={profile.Trailer.OwnerInfo?.OwnerID}
                                        setFocus={true}
                                        onSelectedOwner={(e) => onChange(e)}
                                    />}
                                save={async (data) => {
                                    if (data == null && window.confirm("Remove Owner?") == false) {
                                        return Promise.resolve();
                                    }
                                    await saveAsyncProfile({ OwnerInfo: data });
                                }}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LicensePlate" className="col-sm-2 col-form-label">License Plate</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 30 }}
                                data={profile.Trailer.LicensePlate}
                                save={async (data) => await saveAsyncProfile({ LicensePlate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LicenseState" className="col-sm-2 col-form-label">License State</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 2, placeholder: 'Two-Digit State Code' }}
                                data={profile.Trailer.LicenseState}
                                save={async (data) => await saveAsyncProfile({ LicenseState: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LicenseExpiryDate" className="col-sm-2 col-form-label">License Expiries</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.LicenseExpiryDate != null ? dayjs.utc(profile.Trailer.LicenseExpiryDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ LicenseExpiryDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="TrailerBaseLicense" className="col-sm-2 col-form-label">Base License</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 15 }}
                                data={profile.Trailer.TrailerBaseLicense}
                                save={async (data) => await saveAsyncProfile({ TrailerBaseLicense: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="InsuredBy" className="col-sm-2 col-form-label">Insured By</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 30 }}
                                data={profile.Trailer.InsuredBy}
                                save={async (data) => await saveAsyncProfile({ InsuredBy: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="InsuranceExpiryDate" className="col-sm-2 col-form-label">Insurance Expiries</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.InsuranceExpiryDate != null ? dayjs.utc(profile.Trailer.InsuranceExpiryDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ InsuranceExpiryDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="InsurancePolicy" className="col-sm-2 col-form-label">Insurance Policy #</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 30 }}
                                data={profile.Trailer.InsurancePolicy}
                                save={async (data) => await saveAsyncProfile({ InsurancePolicy: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="OwnedBy" className="col-sm-2 col-form-label">Owned By</label>
                        <div className="col-sm-10">
                            <EditableField
                                data={profile.Trailer.OwnedBy}
                                renderDisplay={(data) => OwnedBy.find(x => x.ID == data).Name}
                                renderEditable={(data, saving, onChange) => <select disabled={saving} className="custom-select" value={data} onChange={(e) => onChange(parseInt(e.target.value))}>
                                    {OwnedBy.map(({ ID, Name }) => <option key={ID} value={ID}>{Name}</option>)}
                                </select>}
                                save={async (data) => await saveAsyncProfile({ OwnedBy: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LeasedFrom" className="col-sm-2 col-form-label">Leased From</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 30 }}
                                data={profile.Trailer.LeasedFrom}
                                save={async (data) => await saveAsyncProfile({ LeasedFrom: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LeaseStartDate" className="col-sm-2 col-form-label">Lease Start</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.LeaseStartDate != null ? dayjs.utc(profile.Trailer.LeaseStartDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ LeaseStartDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LeaseExpiryDate" className="col-sm-2 col-form-label">Lease Expire</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.LeaseExpiryDate != null ? dayjs.utc(profile.Trailer.LeaseExpiryDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ LeaseExpiryDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="LastMthlyInspDate" className="col-sm-2 col-form-label">Last Inspection</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.LastMthlyInspDate != null ? dayjs.utc(profile.Trailer.LastMthlyInspDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ LastMthlyInspDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="NextInspectionDate" className="col-sm-2 col-form-label">Next Inspection</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.NextInspectionDate != null ? dayjs.utc(profile.Trailer.NextInspectionDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ NextInspectionDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="NextPMDate" className="col-sm-2 col-form-label">Next PM Date</label>
                        <div className="col-sm-10">
                            <EditableField
                                required
                                dataType="date"
                                data={profile.Trailer.NextPMDate != null ? dayjs.utc(profile.Trailer.NextPMDate).tz("America/New_York").toDate() : null}
                                save={async (data) => await saveAsyncProfile({ NextPMDate: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="Comment1" className="col-sm-2 col-form-label">Comment 1</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 60 }}
                                data={profile.Trailer.Comment1}
                                save={async (data) => await saveAsyncProfile({ Comment1: data })}
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="Comment2" className="col-sm-2 col-form-label">Comment 2</label>
                        <div className="col-sm-10">
                            <EditableField
                                dataType="string"
                                inputProps={{ maxLength: 60 }}
                                data={profile.Trailer.Comment2}
                                save={async (data) => await saveAsyncProfile({ Comment2: data })}
                            />
                        </div>
                    </div>
                    <AuditInfo audit={profile.Trailer.AuditInfo} />
                </div>
            </div>
        </>);
    };

    if (props.isDialog) {
        return <Dialog
            className="dialog-w11/12"
            title={<Link to={`/Assets/Trailer/${props.trailerId}`}>Trailer Profile {profile?.Trailer.TrailerNumber ?? ''}</Link>}
            onClose={() => {
                props.onClose();
            }}
        >
            {dataView()}
        </Dialog>
    }

    return dataView();
}

export default TrailerProfile;